import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
  Model,
  getFormattedDate,
  createVoting,
  ToastType,
  MIN_INPUT_LENGTH,
  MAX_PDF_SIZE,
  FORM_ERROR_MESSAGE,
  getDateObject,
  VotingDraftDetails,
  getOrganizerVotings,
  DRAFTS_LIMIT,
  CreateVotingParams,
  VOTING_STATUSES,
  getApiParamsDate
} from "@lubbezposrednio-frontend/core";
import { VotingQuestionsModel } from "./components/VotingQuestions/VotingQuestionsModel";
import { EligibleVotersModel } from "./components/EligibleVoters/EligibleVotersModel";
import { VotingDataModel } from "./components/VotingData/VotingDataModel";
import { NavigateFunction } from "react-router-dom";

export enum CREATE_VOTING_ERR_KEYS {
  IS_PUBLIC,
  REGULATIONS,
  PRIVACY_POLICY
}

// „Goal-first": szablony pre-wypełniają wszystkie wymiary głosowania sensownymi domyślnymi.
export type VotingGoal = "board_resolution" | "election" | "open_referendum" | "poll" | "consultation" | "budget" | "hoa" | "petition" | "custom";

type VotingTemplate = {
  isPublic?: boolean;
  isSecret: boolean;
  electorateType: string;
  requiresVerification: boolean;
  accountRequired: boolean;
  voteConfirmation: string;
  walletPolicy: string;
  quorumPercent?: number;
};

const VOTING_TEMPLATES: Record<Exclude<VotingGoal, "custom">, VotingTemplate> = {
  // Uchwała organu (zarząd/rada/walne): niepubliczna, jawna imienna, lista imienna, konto, kworum 50%.
  board_resolution: { isPublic: false, isSecret: false, electorateType: "closed", requiresVerification: false, accountRequired: true, voteConfirmation: "in_session", walletPolicy: "none", quorumPercent: 50 },
  // Wybory (elekcja): wybór osób z listy — lista imienna, TAJNE, konto, bez kworum.
  election: { isPublic: false, isSecret: true, electorateType: "closed", requiresVerification: false, accountRequired: true, voteConfirmation: "in_session", walletPolicy: "none", quorumPercent: undefined },
  // Referendum otwarte: publiczne, tajne (ZK), elektorat otwarty, weryfikacja wymuszona.
  open_referendum: { isPublic: true, isSecret: true, electorateType: "open", requiresVerification: true, accountRequired: false, voteConfirmation: "email", walletPolicy: "none", quorumPercent: undefined },
  // Ankieta / sondaż: nieformalne, jawne, zapisy, bez weryfikacji i bez kworum.
  poll: { isPublic: true, isSecret: false, electorateType: "signup", requiresVerification: false, accountRequired: false, voteConfirmation: "in_session", walletPolicy: "none", quorumPercent: undefined },
  // Konsultacje społeczne: publiczne, jawne, zapisy, wiele pytań, bez twardego kworum.
  consultation: { isPublic: true, isSecret: false, electorateType: "signup", requiresVerification: false, accountRequired: false, voteConfirmation: "in_session", walletPolicy: "none", quorumPercent: undefined },
  // Budżet obywatelski: publiczne, tajne, zapisy mieszkańców, weryfikacja (1 osoba = 1 głos), bez kworum.
  budget: { isPublic: true, isSecret: true, electorateType: "signup", requiresVerification: true, accountRequired: false, voteConfirmation: "email", walletPolicy: "none", quorumPercent: undefined },
  // Uchwała wspólnoty/spółdzielni: niepubliczna, jawna imienna, lista lokali, konto, kworum 50%.
  hoa: { isPublic: false, isSecret: false, electorateType: "closed", requiresVerification: false, accountRequired: true, voteConfirmation: "in_session", walletPolicy: "none", quorumPercent: 50 },
  // Petycja / poparcie: publiczna, jawny podpis, elektorat otwarty, weryfikacja wymuszona, bez kworum.
  petition: { isPublic: true, isSecret: false, electorateType: "open", requiresVerification: true, accountRequired: false, voteConfirmation: "email", walletPolicy: "none", quorumPercent: undefined }
};

export type WizardStepDef = { key: string; label: string };
const ALL_STEPS: WizardStepDef[] = [
  { key: "type", label: "Typ" },
  { key: "data", label: "Dane" },
  { key: "questions", label: "Pytania" },
  { key: "voters", label: "Uprawnieni" },
  { key: "rules", label: "Zasady" }
];

export class CreateVotingViewModel extends Model {
  isPublic?: boolean = false; // domyślna widoczność: niepubliczne
  isSecret = true;
  requiresVerification = false; // domyślnie bez weryfikacji tożsamości (referendum „open" wymusi true)
  accountRequired = false; // „tylko z kontami" — głosować mogą wyłącznie zalogowane konta
  voteConfirmation = "in_session";
  walletPolicy = "none"; // Faza D: none|recommended|required — podpis tożsamości przy potwierdzaniu jawnych
  verifiedIdentityRequired = false; // wymagaj zweryfikowanej tożsamości CyberID (per głosowanie, bez konta)
  minAssuranceLevel = 2;            // minimalny poziom Aury CyberID (A2=dokument/mDowód, A3=mObywatel)
                          // (token-only nadal działa dla none/recommended; required wymaga ważnego podpisu)
  electorateType = "closed";
  signupClosesAt = "";
  quorumPercent?: number;
  votingGoal: VotingGoal = "custom"; // wybrany szablon / „od zera"
  verificationForcedByOpen = false; // referendum wymusiło weryfikację — pozwala cofnąć po zejściu z „open"
  isPublishing = false; // guard przeciw podwójnej publikacji + spinner na przycisku
  publishError?: string;
  customPrivacyPolicyUrl?: string;
  customPrivacyPolicy?: File;
  customRegulationsUrl?: string;
  customRegulations?: File;
  errors: Record<CREATE_VOTING_ERR_KEYS, string> = {} as Record<CREATE_VOTING_ERR_KEYS, string>;
  votingQuestionsModel: VotingQuestionsModel = new VotingQuestionsModel();
  eligibleVotersModel: EligibleVotersModel = new EligibleVotersModel();
  votingDataModel: VotingDataModel = new VotingDataModel();
  confirmData: {
    isVisible: boolean;
    startDate?: string;
    endDate?: string;
    votingId?: string;
    votingGoal?: VotingGoal;
    isPublic?: boolean;
    title?: string;
    isSecret?: boolean;
    electorateType?: string;
    questionsCount?: number;
    votersCount?: number;
    quorumPercent?: number;
  } = { isVisible: false };
  draftsQuantity = 0;

  constructor() {
    super();

    makeObservable(this, {
      isPublic: observable,
      setIsPublic: action.bound,
      isSecret: observable,
      setIsSecret: action.bound,
      requiresVerification: observable,
      setRequiresVerification: action.bound,
      accountRequired: observable,
      setAccountRequired: action.bound,
      verifiedIdentityRequired: observable,
      setVerifiedIdentityRequired: action.bound,
      minAssuranceLevel: observable,
      setMinAssuranceLevel: action.bound,
      voteConfirmation: observable,
      setVoteConfirmation: action.bound,
      walletPolicy: observable,
      setWalletPolicy: action.bound,
      maxWalletPolicy: computed,
      electorateType: observable,
      setElectorateType: action.bound,
      preselectGroup: action.bound,
      votingGoal: observable,
      verificationForcedByOpen: observable,
      isPublishing: observable,
      applyTemplate: action.bound,
      visibleSteps: computed,
      validationIssues: computed,
      isReadyForSummary: computed,
      signupClosesAt: observable,
      setSignupClosesAt: action.bound,
      quorumPercent: observable,
      setQuorum: action.bound,
      customPrivacyPolicy: observable.ref,
      setCustomPrivacyPolicy: action.bound,
      customRegulations: observable.ref,
      setCustomRegulations: action.bound,
      votingQuestionsModel: observable.ref,
      eligibleVotersModel: observable.ref,
      votingDataModel: observable.ref,
      confirmData: observable.ref,
      customPrivacyPolicyUrl: observable,
      customRegulationsUrl: observable,
      draftsQuantity: observable,
      loadDataFromDraft: action.bound,

      errors: observable,
      saveError: action.bound,
      isError: computed,

      clearData: action.bound,
      clearErrors: action.bound,
      customRegulationsUrlName: computed,
      customPrivacyPolicyUrlName: computed
    });
  }

  public loadDataFromDraft(draft: VotingDraftDetails) {
    this.setIsPublic(draft.isPublic);
    this.votingDataModel.loadDataFromDraft(draft);
    this.votingQuestionsModel.loadDataFromDraft(draft);
    this.eligibleVotersModel.loadDataFromDraft(draft);
    this.customRegulationsUrl = draft.termsAndConditionsUrl;
    this.customPrivacyPolicyUrl = draft.privacyPolicyUrl;
  }

  setIsPublic(isPublic?: boolean) {
    this.isPublic = isPublic;
    this.errors[CREATE_VOTING_ERR_KEYS.IS_PUBLIC] && this.validateIsPublic();
  }

  setIsSecret(value: boolean) {
    this.isSecret = value;
  }

  setRequiresVerification(value: boolean) {
    this.requiresVerification = value;
  }

  setAccountRequired(value: boolean) {
    this.accountRequired = value;
  }

  setVerifiedIdentityRequired(value: boolean) {
    this.verifiedIdentityRequired = value;
  }

  setMinAssuranceLevel(value: number) {
    this.minAssuranceLevel = value === 3 ? 3 : 2;
  }

  setVoteConfirmation(value: string) {
    this.voteConfirmation = value;
    // KASKADA: portfel (Secure Enclave) istnieje TYLKO w aplikacji — web/sesja i mail nie podpiszą.
    // Obniżenie kanału auto-degraduje politykę tożsamości do dozwolonego maksimum.
    if (!this.walletPolicyAllowed(this.walletPolicy)) {
      this.walletPolicy = this.maxWalletPolicy;
    }
  }

  // Najwyższa dozwolona polityka tożsamości dla bieżącego kanału potwierdzania:
  //  in_session (web/panel) → none  ·  email (mail|apka) → recommended  ·  app (tylko apka) → required
  get maxWalletPolicy(): string {
    if (this.voteConfirmation === "app") return "required";
    if (this.voteConfirmation === "email") return "recommended";
    return "none";
  }

  walletPolicyAllowed(value: string): boolean {
    const order = ["none", "recommended", "required"];
    return order.indexOf(value) <= order.indexOf(this.maxWalletPolicy);
  }

  setWalletPolicy(value: string) {
    // strażnik: nie pozwól ustawić poziomu wyższego niż dopuszcza kanał (gdyby UI nie zablokowało)
    this.walletPolicy = this.walletPolicyAllowed(value) ? value : this.maxWalletPolicy;
  }

  setElectorateType(value: string) {
    this.electorateType = value;
    // referendum (otwarte) wymaga weryfikacji tożsamości; zapamiętaj że to wymuszenie (odwracalne po zejściu z „open")
    if (value === "open") {
      this.requiresVerification = true;
      this.verificationForcedByOpen = true;
    } else {
      this.verificationForcedByOpen = false;
    }
  }

  // „Goal-first": nałóż szablon (wszystkie wymiary jawnie — nie polega na clearData).
  applyTemplate(goal: VotingGoal) {
    this.votingGoal = goal;
    if (goal === "custom") return;
    const t = VOTING_TEMPLATES[goal];
    this.isPublic = t.isPublic;
    this.isSecret = t.isSecret;
    this.electorateType = t.electorateType;
    this.requiresVerification = t.requiresVerification;
    this.accountRequired = t.accountRequired;
    this.verifiedIdentityRequired = (t as { verifiedIdentityRequired?: boolean }).verifiedIdentityRequired ?? false;
    this.minAssuranceLevel = (t as { minAssuranceLevel?: number }).minAssuranceLevel ?? 2;
    this.voteConfirmation = t.voteConfirmation;
    this.walletPolicy = t.walletPolicy;
    this.quorumPercent = t.quorumPercent;
    this.verificationForcedByOpen = t.electorateType === "open";
    this.clearErrors();
  }

  // Kroki widoczne (branching): referendum „open" nie ma kroku listy uprawnionych.
  get visibleSteps(): WizardStepDef[] {
    return this.electorateType === "open" ? ALL_STEPS.filter(s => s.key !== "voters") : ALL_STEPS;
  }

  // Wstępny wybór grupy (gdy głosowanie zakładane z poziomu kompozera grupy).
  public preselectGroup(id: string) {
    this.setElectorateType("closed");
    if (!this.eligibleVotersModel.selectedGroupIds.includes(id)) {
      this.eligibleVotersModel.toggleGroup(id);
    }
  }

  setSignupClosesAt(value: string) {
    this.signupClosesAt = value;
  }

  setQuorum(value?: number) {
    this.quorumPercent = value;
  }

  setCustomPrivacyPolicy(customPrivacyPolicy?: File) {
    this.customPrivacyPolicy = customPrivacyPolicy;
    this.customPrivacyPolicyUrl = undefined;
    this.errors[CREATE_VOTING_ERR_KEYS.PRIVACY_POLICY] && this.validateCustomDocuments();
  }

  setCustomRegulations(customRegulations?: File) {
    this.customRegulations = customRegulations;
    this.customRegulationsUrl = undefined;
    this.errors[CREATE_VOTING_ERR_KEYS.REGULATIONS] && this.validateCustomDocuments();
  }

  private validateIsPublic() {
    this.saveError(CREATE_VOTING_ERR_KEYS.IS_PUBLIC, this.isPublic !== undefined, "Wybierz typ głosowania");
  }

  private validateDocumentSize(document?: File): boolean {
    return (document?.size ?? 0) <= MAX_PDF_SIZE;
  }

  private validateCustomDocuments() {
    const documentValidations = [
      {
        key: CREATE_VOTING_ERR_KEYS.REGULATIONS,
        value: this.customRegulations
      },
      {
        key: CREATE_VOTING_ERR_KEYS.PRIVACY_POLICY,
        value: this.customPrivacyPolicy
      }
    ];

    documentValidations.forEach(({ key, value }) => {
      this.saveError(
        key,
        this.validateDocumentSize(value),
        `Maksymalna wielkość pliku to ${MAX_PDF_SIZE / 1024 / 1024} MB`
      );
    });
  }

  saveError(key: CREATE_VOTING_ERR_KEYS, condition: boolean, message: string): boolean {
    this.errors[key] = condition ? "" : message;
    return condition;
  }

  validateFields() {
    this.validateIsPublic();
    this.votingDataModel?.validateFields();
    this.electorateType === "closed" && this.eligibleVotersModel?.validateFields();
    this.validateCustomDocuments();
  }

  // walidacja pojedynczego kroku wizarda (przy "Dalej")
  validateStep(step: number): boolean {
    switch (step) {
      case 0:
        this.validateIsPublic();
        return !this.errors[CREATE_VOTING_ERR_KEYS.IS_PUBLIC];
      case 1:
        this.votingDataModel.validateFields();
        return !this.votingDataModel.isError;
      case 2:
        this.votingQuestionsModel.handleSumUp();
        return !this.votingQuestionsModel.isError;
      case 3:
        if (this.electorateType === "closed") {
          this.eligibleVotersModel.validateFields();
          return !this.eligibleVotersModel.error;
        }
        return true;
      case 4:
        this.validateCustomDocuments();
        return !this.errors[CREATE_VOTING_ERR_KEYS.REGULATIONS] && !this.errors[CREATE_VOTING_ERR_KEYS.PRIVACY_POLICY];
      default:
        return true;
    }
  }

  // Pełna lista problemów walidacji — CZYSTE (bez ustawiania błędów). Każdy z precyzyjnym komunikatem i „target" do skoku.
  get validationIssues(): { target: "name" | "term" | "questions" | "voters" | "docs"; message: string }[] {
    const issues: { target: "name" | "term" | "questions" | "voters" | "docs"; message: string }[] = [];
    const d = this.votingDataModel;
    const complete = (rec: Record<string, string>) => Object.values(rec).every(v => v !== "" && v != null);

    // ── Nazwa ──
    const name = d.name.trim();
    if (!name) issues.push({ target: "name", message: "Podaj nazwę głosowania." });
    else if (name.length < MIN_INPUT_LENGTH) issues.push({ target: "name", message: `Nazwa musi mieć min. ${MIN_INPUT_LENGTH} znaki.` });

    // ── Termin ──
    const startC = complete(d.startVotingDate), endC = complete(d.endVotingDate);
    if (!startC) issues.push({ target: "term", message: "Uzupełnij pełną datę i godzinę rozpoczęcia." });
    if (!endC) issues.push({ target: "term", message: "Uzupełnij pełną datę i godzinę zakończenia." });
    if (startC && endC) {
      const s = getDateObject(d.startVotingDate).getTime();
      const e = getDateObject(d.endVotingDate).getTime();
      if (s <= Date.now()) issues.push({ target: "term", message: "Rozpoczęcie musi być w przyszłości." });
      else if (e <= s) issues.push({ target: "term", message: "Zakończenie musi być po rozpoczęciu." });
    }

    // ── Pytania ──
    const qs = this.votingQuestionsModel.questionsArray;
    const started = qs.filter(q => q.question?.trim()).length > 0;
    if (!started) {
      issues.push({ target: "questions", message: "Dodaj przynajmniej jedno pytanie z treścią." });
    } else {
      qs.forEach((q, i) => {
        const n = i + 1;
        const t = (q.question ?? "").trim();
        if (!t) { issues.push({ target: "questions", message: `Pytanie ${n}: wpisz treść (min. ${MIN_INPUT_LENGTH} znaków).` }); return; }
        if (t.length < MIN_INPUT_LENGTH) issues.push({ target: "questions", message: `Pytanie ${n}: treść musi mieć min. ${MIN_INPUT_LENGTH} znaki.` });
        if (t.length > 500) issues.push({ target: "questions", message: `Pytanie ${n}: treść może mieć maks. 500 znaków.` });
        if (q.qtype !== "yes_no") {
          const answers = q.answers ?? [];
          const filledA = answers.filter(a => a.content?.trim());
          const blankA = answers.filter(a => !a.content?.trim());
          if (filledA.length < 2) issues.push({ target: "questions", message: `Pytanie ${n}: dodaj co najmniej 2 odpowiedzi.` });
          else if (blankA.length > 0) issues.push({ target: "questions", message: `Pytanie ${n}: uzupełnij lub usuń puste odpowiedzi.` });
          if (filledA.some(a => (a.content?.trim().length ?? 0) > 300)) issues.push({ target: "questions", message: `Pytanie ${n}: odpowiedź może mieć maks. 300 znaków.` });
          if (q.qtype === "multiple" && (q.maxSelections == null || q.maxSelections < 2)) issues.push({ target: "questions", message: `Pytanie ${n}: ustaw maksymalną liczbę wyborów (min. 2).` });
        }
        if (q.thresholdType === "custom" && (q.thresholdPercent == null || q.thresholdPercent < 0 || q.thresholdPercent > 100)) {
          issues.push({ target: "questions", message: `Pytanie ${n}: podaj próg przyjęcia (0–100%).` });
        }
      });
    }

    // ── Uprawnieni (tylko zamknięta lista) ──
    if (this.electorateType === "closed") {
      const ev = this.eligibleVotersModel;
      if (ev.totalCount === 0 && !ev.hasCsv && ev.selectedGroupIds.length === 0) {
        issues.push({ target: "voters", message: "Dodaj uprawnionych: wyszukaj konta, wybierz grupę lub wgraj listę CSV." });
      }
      if (ev.votersCsv && ev.votersCsv.size > 2 * 1024 * 1024) {
        issues.push({ target: "voters", message: "Plik CSV przekracza dopuszczalne 2 MB." });
      }
    }

    // ── Kworum ──
    if (this.quorumPercent != null && (this.quorumPercent < 0 || this.quorumPercent > 100)) {
      issues.push({ target: "docs", message: "Kworum musi być z zakresu 0–100%." });
    }

    // ── Dokumenty (rozmiar PDF) ──
    if (this.customRegulations && this.customRegulations.size > MAX_PDF_SIZE) {
      issues.push({ target: "docs", message: `Regulamin: plik przekracza dozwolone ${Math.round(MAX_PDF_SIZE / 1024 / 1024)} MB.` });
    }
    if (this.customPrivacyPolicy && this.customPrivacyPolicy.size > MAX_PDF_SIZE) {
      issues.push({ target: "docs", message: `Polityka prywatności: plik przekracza dozwolone ${Math.round(MAX_PDF_SIZE / 1024 / 1024)} MB.` });
    }

    return issues;
  }

  // Czy głosowanie jest gotowe do podsumowania — brak problemów walidacji.
  get isReadyForSummary(): boolean {
    return this.validationIssues.length === 0;
  }

  // Pierwszy niepoprawny krok (do precyzyjnego komunikatu „co dokładnie jest nie tak").
  firstInvalidStep(): { index: number; label: string } | null {
    const labels = ["Typ", "Dane", "Pytania", "Uprawnieni", "Zasady"];
    for (let i = 0; i < labels.length; i++) {
      if (!this.validateStep(i)) return { index: i, label: labels[i] };
    }
    return null;
  }

  public handleSumUp(navigate: NavigateFunction, toast: ToastType) {
    this.confirmData.isVisible &&
      runInAction(() => {
        this.confirmData = { isVisible: false };
      });

    this.votingQuestionsModel?.handleSumUp();
    this.validateFields();

    if (!this.isError) {
      return navigate("/organizator/utworz-glosowanie/podsumowanie");
    }
    this.showGenericErrorToast(toast, FORM_ERROR_MESSAGE);
  }

  async getDraftsQuantity() {
    try {
      const { data } = await getOrganizerVotings({ status: VOTING_STATUSES.DRAFT, per_page: 1 });
      const quantity = data.meta.total_pages;
      quantity &&
        runInAction(() => {
          this.draftsQuantity = quantity;
        });
    } catch (err) {
      console.error(err);
    }
  }

  private getVotingParams(isDraft?: boolean): Partial<CreateVotingParams> {
    const start = getApiParamsDate(this.votingDataModel.startVotingDate, isDraft);
    const end = getApiParamsDate(this.votingDataModel.endVotingDate, isDraft);

    const isEmptyQuestions = this.votingQuestionsModel.questionsArray.every(
      question => !question.question && question.answers.every(answer => !answer.content)
    );
    // „Wiele wyborów" wymaga max_selections (≥2) przy publikacji — domyślamy liczbą wypełnionych odpowiedzi.
    const rawQuestions = this.votingQuestionsModel.questionsArray.map(q => {
      if (q.qtype === "multiple" && (q.maxSelections == null || q.maxSelections < 2)) {
        const filled = q.answers.filter(a => a.content?.trim()).length;
        return { ...q, maxSelections: filled >= 2 ? filled : 2 };
      }
      return q;
    });
    const questions = !isDraft || !isEmptyQuestions ? rawQuestions : undefined;

    const isPublic = !isDraft || this.isPublic !== undefined ? String(!!this.isPublic) : undefined;

    // listy uprawnionych wysyłamy tylko dla elektoratu "zamknięta lista"
    const isClosed = this.electorateType === "closed";

    return {
      title: this.votingDataModel.name,
      description: this.votingDataModel.description?.trim() || undefined,
      public: isPublic,
      start_date: start,
      end_date: end,
      privacy_policy: this.customPrivacyPolicy,
      privacy_policy_url: this.customPrivacyPolicyUrl,
      terms_and_conditions: this.customRegulations,
      terms_and_conditions_url: this.customRegulationsUrl,
      voters_list_url: isClosed ? this.eligibleVotersModel.votersCsvUrl || undefined : undefined,
      voters_list_file: isClosed ? this.eligibleVotersModel.votersCsv || undefined : undefined,
      emails: isClosed && this.eligibleVotersModel.votersEmails.length ? this.eligibleVotersModel.votersEmails : undefined,
      group_ids:
        isClosed && this.eligibleVotersModel.selectedGroupIds.length
          ? this.eligibleVotersModel.selectedGroupIds
          : undefined,
      questions,
      published: String(!isDraft),
      secret: String(this.isSecret),
      requires_verification: String(this.requiresVerification),
      account_required: String(this.accountRequired),
      verified_identity_required: String(this.verifiedIdentityRequired),
      min_assurance_level: String(this.minAssuranceLevel),
      vote_confirmation: this.voteConfirmation,
      // wallet_policy dotyczy jawnych (podpis przy potwierdzaniu); dla tajnych zawsze none (ZK).
      wallet_policy: this.isSecret ? "none" : this.walletPolicy,
      electorate_type: this.electorateType,
      signup_closes_at: this.electorateType === "signup" && this.signupClosesAt ? this.signupClosesAt : undefined,
      quorum_percent: this.quorumPercent != null ? String(this.quorumPercent) : undefined
    };
  }

  clearErrors() {
    this.errors = {} as Record<CREATE_VOTING_ERR_KEYS, string>;
    this.votingDataModel.clearErrors();
    this.votingQuestionsModel.clearErrors();
    this.eligibleVotersModel.clearErrors();
  }

  private async validateDraft(toast: ToastType): Promise<boolean> {
    this.clearErrors();
    await this.getDraftsQuantity();
    if (this.draftsQuantity >= DRAFTS_LIMIT) {
      this.showGenericErrorToast(
        toast,
        `Maksymalna ilość wersji roboczych to ${DRAFTS_LIMIT}. Aby dodać kolejną wersję roboczą, usuń jedną z uprzednio utworzonych wersji roboczych.`
      );

      return false;
    }

    this.eligibleVotersModel.validateCsvSize();
    this.validateCustomDocuments();
    this.votingDataModel.validateDatesCompleteness(true);

    this.isError && this.showGenericErrorToast(toast, FORM_ERROR_MESSAGE);
    return !this.isError;
  }

  public async handleCreateDraft(navigate: NavigateFunction, toast: ToastType) {
    const isValid = await this.validateDraft(toast);

    if (isValid) {
      const errMessage = "Nie udało się utworzyć wersji roboczej. Spróbuj ponownie później.";
      try {
        const response = await createVoting(this.getVotingParams(true));

        if (response?.status === 200) {
          toast({ status: "success", isClosable: true, title: "Wersja robocza została utworzona" });
          navigate("/organizator/twoje-glosowania");
        } else {
          this.showGenericErrorToast(toast, errMessage);
        }
      } catch (err) {
        this.showGenericErrorToast(toast, errMessage);
        console.error(err);
      }
    }
  }

  public async handleCreateVoting(toast: ToastType) {
    if (this.isPublishing) return; // guard: brak podwójnej publikacji
    runInAction(() => {
      this.isPublishing = true;
      this.publishError = undefined;
    });
    const errMsg = "Nie udało się utworzyć głosowania. Spróbuj ponownie później.";
    try {
      const response = await createVoting(this.getVotingParams() as CreateVotingParams);
      if (response?.status === 200 || response?.status === 201) {
        const startDate = getFormattedDate(getDateObject(this.votingDataModel.startVotingDate));
        const endDate = getFormattedDate(getDateObject(this.votingDataModel.endVotingDate));
        const votingId = (response as { data?: { id?: string } })?.data?.id;
        // Snapshot danych PRZED clearData — ekran sukcesu pokazuje pełne dane + QR.
        const snapshot = {
          startDate,
          endDate,
          votingId,
          votingGoal: this.votingGoal,
          isPublic: this.isPublic,
          title: this.votingDataModel.name,
          isSecret: this.isSecret,
          electorateType: this.electorateType,
          questionsCount: this.votingQuestionsModel.questionsArray.filter(q => q.question?.trim()).length,
          votersCount: this.eligibleVotersModel.totalCount,
          quorumPercent: this.quorumPercent
        };
        runInAction(() => {
          this.confirmData = { isVisible: true, ...snapshot };
        });
        this.clearData();
      } else {
        runInAction(() => (this.publishError = errMsg));
        toast({ title: errMsg, status: "error", isClosable: true });
      }
    } catch (err) {
      console.error(err);
      const msg = this.extractApiError(err) || errMsg;
      runInAction(() => (this.publishError = msg));
      toast({ title: msg, status: "error", isClosable: true });
    } finally {
      runInAction(() => {
        this.isPublishing = false;
      });
    }
  }

  // Wyciąga czytelny komunikat z odpowiedzi błędu (422 → walidacje z backendu).
  private extractApiError(err: unknown): string | undefined {
    const data = (err as { response?: { data?: { errors?: unknown; error?: string } } })?.response?.data;
    if (!data) return undefined;
    if (typeof data.error === "string") return data.error;
    const errors = data.errors;
    if (!errors) return undefined;
    const list = Array.isArray(errors)
      ? errors.map(String)
      : Object.entries(errors as Record<string, unknown>).flatMap(([key, val]) =>
          (Array.isArray(val) ? val : [val]).map(v => `${key.includes(".") ? key.split(".").pop() : key}: ${v}`)
        );
    return list.length ? list.join(". ") : undefined;
  }

  clearData() {
    this.isPublic = undefined;
    this.isSecret = true;
    this.requiresVerification = false; // było błędnie true (drift vs konstruktor) — przeciek na kolejne głosowanie
    this.accountRequired = false; // było pomijane — przeciek
    this.verifiedIdentityRequired = false;
    this.minAssuranceLevel = 2;
    this.voteConfirmation = "in_session";
    this.walletPolicy = "none";
    this.electorateType = "closed";
    this.verificationForcedByOpen = false;
    this.votingGoal = "custom";
    this.signupClosesAt = "";
    this.quorumPercent = undefined;
    this.customPrivacyPolicy = undefined;
    this.customRegulations = undefined;
    this.customRegulationsUrl = undefined;
    this.customPrivacyPolicyUrl = undefined;

    this.errors = {} as Record<CREATE_VOTING_ERR_KEYS, string>;

    this.votingQuestionsModel?.clearData();
    this.eligibleVotersModel?.clearData();
    this.votingDataModel?.clearData();
  }

  override async onInit() {}

  get customRegulationsUrlName(): string {
    const lastSlashIndex = this.customRegulationsUrl?.lastIndexOf("/");
    return decodeURI(this.customRegulationsUrl?.slice((lastSlashIndex ?? 0) + 1) ?? "");
  }

  get customPrivacyPolicyUrlName(): string {
    const lastSlashIndex = this.customPrivacyPolicyUrl?.lastIndexOf("/");
    return decodeURI(this.customPrivacyPolicyUrl?.slice((lastSlashIndex ?? 0) + 1) ?? "");
  }

  get isError(): boolean {
    return (
      Object.values(this.errors).some(error => error) ||
      this.votingDataModel?.isError ||
      !!this.eligibleVotersModel?.error ||
      !!this.votingQuestionsModel?.isError
    );
  }
}

export const createVotingModel = new CreateVotingViewModel();
